import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Booking } from '../booking'
import { bookingActions } from '../booking.actions'
import { bookingSelectors } from '../booking.selectors'
import { Actions, ofType } from '@ngrx/effects'
import { NotificationService } from 'src/app/core/notification.service'
import { Participation } from '../participation/participation'
import { participationSelectors } from '../participation/participation.selectors'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import moment from 'moment-mini'
import { metaOptionSelectors } from '../../meta-option/meta-option.selectors'
import { Dictionary } from '@ngrx/entity'
import { Product } from '../../product/product'
import { MetaOptionKeys } from '../../meta-option/meta-option'
import { getAllDaysNotAvailabilityInYearBuffer } from 'src/app/shared/until'
import { UserRole } from '../../role/user-role/user-role'
import { persistentUserRoleSelectors } from '../../role/user-role/persistent-user-role/persistent-user-role.selector'
import { metaOptionActions } from '../../meta-option/meta-option.actions'
import { productSelectors } from '../../product/product.selectors'
import { TranslateService } from '@ngx-translate/core'
import { EmployeeBooking } from '../../employee-booking/employee-booking'
import { employeeBookingSelectors } from '../../employee-booking/employee-booking.selectors'
import { employeeBookingActions } from '../../employee-booking/employee-booking.actions'
import { ConfirmationModalComponent } from '../serie-proposal/series-tab/confirmation-modal/confirmation-modal.component'
import { BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'booking-details-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class BookingDetailsTabComponent implements OnInit {
  @Output() setConflicValue: EventEmitter<any> = new EventEmitter()
  public booking$: Observable<Booking>
  public bookingId$: Observable<number>
  public form: UntypedFormGroup
  public participations$: Observable<Participation[]>
  public generalFormSubmitted: boolean = false
  public productEntities$: Observable<Dictionary<Product>>
  public currentUserRole$: Observable<UserRole>
  public disabledDates: Date[] = []
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.bookingId$ = this.store
      .select(bookingSelectors.selectIdRouteParam)
      .pipe(filter((id) => Boolean(id)))

    this.booking$ = this.bookingId$.pipe(
      switchMap((bookingId) =>
        this.store.select(bookingSelectors.selectById, bookingId)
      )
    )
    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.currentUserRole$ = this.store.select(
      persistentUserRoleSelectors.selectSelectedUserRole
    )
    this.currentUserRole$.pipe(take(1)).subscribe((userRole) =>
      this.store.dispatch(
        metaOptionActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              key: MetaOptionKeys.DEFAULT_YEAR_MATRIX,
              siteId: userRole?.siteId
            }
          })
        })
      )
    )
    this.participations$ = this.booking$.pipe(
      withLatestFrom(this.store.select(participationSelectors.selectEntities)),
      map(([booking, participationEntities]) =>
        booking?.participations.map((pId) => participationEntities[pId])
      )
    )
  }

  private isEmployeeBookingSynced(
    employeeBooking: EmployeeBooking,
    booking: Booking
  ): boolean {
    if (!employeeBooking || !booking) {
      return false
    }

    const bookingStart = moment(booking.startDate)
    const bookingEnd = moment(booking.endDate)
    const ebStart = moment(employeeBooking.startDate)
    const ebEnd = moment(employeeBooking.endDate)

    const expectedEbEnd = moment(bookingEnd).subtract(1, 'minute')

    const startDiff = Math.abs(bookingStart.diff(ebStart, 'minutes'))
    const endDiff = Math.abs(expectedEbEnd.diff(ebEnd, 'minutes'))
    const startMatches = startDiff <= 1
    const endMatches = endDiff <= 1

    return startMatches && endMatches
  }

  public onShowConflicts({
    show,
    arrayBookingsWOIssues,
    arrayBookingsWIssues,
    values
  }) {
    this.store
      .select(metaOptionSelectors.selectAll)
      .pipe(
        withLatestFrom(this.currentUserRole$, this.productEntities$),
        take(1)
      )
      .subscribe(([allMetaOptions, userRole, productEntities]) => {
        const metaOption = allMetaOptions.find(
          (option) =>
            option.key == MetaOptionKeys.DEFAULT_YEAR_MATRIX &&
            option.siteId == userRole?.siteId
        )
        this.disabledDates = []
        this.disabledDates = getAllDaysNotAvailabilityInYearBuffer(
          metaOption?.value ?? null
        )
        arrayBookingsWOIssues = arrayBookingsWOIssues.filter(
          (bookingWOIssue) => {
            const disabledDatesByCalendarProduct =
              getAllDaysNotAvailabilityInYearBuffer(
                productEntities[bookingWOIssue.linkedProductId]
                  ?.yearCalendarBuffer
              )
            const startDate = moment(bookingWOIssue.startTargetDate).format(
              'YYYY-MM-DD'
            )
            const endDate = moment(bookingWOIssue.endTargetDate).format(
              'YYYY-MM-DD'
            )
            const disabledCalendarDatesProduct = new Set(
              disabledDatesByCalendarProduct?.map((dateInCalendar) =>
                moment(dateInCalendar).format('YYYY-MM-DD')
              ) ?? []
            )
            const disabledDates = new Set(
              this.disabledDates?.map((dateInCalendar) =>
                moment(dateInCalendar).format('YYYY-MM-DD')
              ) ?? []
            )
            if (
              !disabledCalendarDatesProduct.has(startDate) &&
              !disabledCalendarDatesProduct.has(endDate) &&
              !disabledDates.has(startDate) &&
              !disabledDates.has(endDate)
            ) {
              return bookingWOIssue
            } else {
              arrayBookingsWIssues.push({
                ...bookingWOIssue,
                yearCalendarIssue: true
              })
            }
          }
        )

        if (values && values.endDate && moment(values.endDate)?.isValid()) {
          values.endDate = moment(values.endDate).subtract(1, 'm')
        }
        this.setConflicValue.emit({
          showConflict: show,
          arrayBookingsWOIssues,
          arrayBookingsWIssues,
          formValues: values
        })
      })
  }
  public onSubmit(form): void {
    combineLatest([this.bookingId$, this.participations$, this.booking$])
      .pipe(
        take(1),
        switchMap(([bookingId, participations, currentBooking]) => {
          if (participations?.length) {
            const message = this.translate.instant(
              'bookingModule.confirmUpdate'
            )
            if (!confirm(message)) {
              return of(null)
            }
          }

          if (
            !currentBooking ||
            !currentBooking.supervisors ||
            currentBooking.supervisors.length === 0
          ) {
            return of({
              bookingId,
              updateEmployeeBookings: false,
              relatedEmployeeBookings: [],
              productEntities: {},
              currentBooking
            })
          }

          this.store.dispatch(bookingActions.actionFinishedReset())

          this.store.dispatch(
            bookingActions.getAllEmployeeRelations({
              data: {
                type: 'booking',
                withSerie: false,
                bookingId: bookingId,
                personIds: currentBooking.supervisors
              }
            })
          )

          return this.store.select(bookingSelectors.selectActionFinished).pipe(
            filter(
              (itemAction) =>
                itemAction !== null &&
                itemAction.action ===
                  bookingActions.getAllEmployeeRelations.type
            ),
            take(1),
            timeout(5000),
            catchError(() => of(null)),
            switchMap(() =>
              combineLatest([
                this.store.select(employeeBookingSelectors.selectAll),
                this.store.select(productSelectors.selectEntities)
              ]).pipe(take(1))
            ),
            switchMap(([employeeBookings, productEntities]) => {
              const relatedEmployeeBookings = employeeBookings.filter(
                (eb) =>
                  eb.description &&
                  eb.description.includes(`booking ${bookingId}`) &&
                  this.isEmployeeBookingSynced(eb, currentBooking)
              )

              if (relatedEmployeeBookings.length > 0) {
                return new Observable((observer) => {
                  const initialState = {
                    title: 'bookingModule.updateRelatedEmployeeBookingsTitle',
                    message:
                      'bookingModule.updateRelatedEmployeeBookingsWarning',
                    buttons: [
                      {
                        text: this.translate.instant('general.yes'),
                        class: 'btn-primary',
                        accept: true
                      },
                      {
                        text: this.translate.instant('general.no'),
                        class: 'btn-secondary',
                        accept: false
                      }
                    ]
                  }

                  const modalRef = this.modalService.show(
                    ConfirmationModalComponent,
                    {
                      initialState,
                      class: 'modal-dialog-centered'
                    }
                  )

                  const modalComponent =
                    modalRef.content as ConfirmationModalComponent
                  const acceptSub = modalComponent.onAccept.subscribe(
                    (updateEmployeeBookings: boolean) => {
                      observer.next({
                        bookingId,
                        updateEmployeeBookings,
                        relatedEmployeeBookings,
                        productEntities,
                        currentBooking
                      })
                      observer.complete()
                      acceptSub.unsubscribe()
                    }
                  )

                  const hiddenSub = this.modalService.onHidden
                    .pipe(take(1))
                    .subscribe(() => {
                      if (!acceptSub.closed) {
                        observer.complete()
                        acceptSub.unsubscribe()
                      }
                      hiddenSub.unsubscribe()
                    })
                })
              } else {
                return of({
                  bookingId,
                  updateEmployeeBookings: false,
                  relatedEmployeeBookings: [],
                  productEntities,
                  currentBooking
                })
              }
            })
          )
        })
      )
      .subscribe(
        (
          result: {
            bookingId: number
            updateEmployeeBookings: boolean
            relatedEmployeeBookings: EmployeeBooking[]
            productEntities: Dictionary<Product>
            currentBooking: Booking
          } | null
        ) => {
          if (!result) return

          const {
            bookingId,
            updateEmployeeBookings,
            relatedEmployeeBookings,
            productEntities,
            currentBooking
          } = result

          this.store.dispatch(
            bookingActions.updateOneById({
              id: bookingId,
              params: {
                ...form,
                status: form.status !== null ? parseInt(form.status) : null,
                id: parseInt(bookingId.toString())
              }
            })
          )

          if (updateEmployeeBookings) {
            combineLatest([
              this.store.select(employeeBookingSelectors.selectAll),
              this.booking$
            ])
              .pipe(take(1))
              .subscribe(([allEmployeeBookings, updatedBooking]) => {
                const currentRelatedEmployeeBookings =
                  allEmployeeBookings.filter(
                    (eb) =>
                      eb.description &&
                      eb.description.includes(`booking ${bookingId}`) &&
                      this.isEmployeeBookingSynced(eb, currentBooking)
                  )

                if (currentRelatedEmployeeBookings.length > 0) {
                  const newStartDate = form.startDate
                    ? moment(form.startDate)
                    : moment(currentBooking.startDate)

                  const newEndDate = form.endDate
                    ? moment(form.endDate)
                    : moment(currentBooking.endDate)

                  const adjustedEndDate = moment(newEndDate).subtract(
                    1,
                    'minute'
                  )

                  currentRelatedEmployeeBookings.forEach((eb) => {
                    this.store.dispatch(
                      employeeBookingActions.updateOneById({
                        id: eb.id,
                        params: {
                          id: eb.id,
                          personId: eb.personId,
                          startDate: newStartDate.toISOString(),
                          endDate: adjustedEndDate.toISOString()
                        }
                      })
                    )
                  })
                }
              })
          }

          if (form.repeat) {
            this.generalFormSubmitted = true
          }

          this.actions$
            .pipe(
              ofType(bookingActions.upsertMany),
              take(1),
              timeout(2500),
              catchError((error) => of(null))
            )
            .subscribe((action) => {
              this.store.dispatch(
                bookingActions.fetchOneById({
                  id: bookingId,
                  params: RequestQueryBuilder.create({
                    join: [
                      { field: 'tags' },
                      { field: 'participations' },
                      { field: 'participations.member' },
                      { field: 'participations.organization' },
                      { field: 'linkedProduct' },
                      { field: 'product' },
                      { field: 'allowedLinkedProducts' },
                      { field: 'course' },
                      { field: 'product.translations' },
                      { field: 'product.dependantProducts' },
                      { field: 'supervisors' },
                      { field: 'documents' },
                      { field: 'allowedLinkedProducts.translations' }
                    ]
                  })
                })
              )

              action
                ? this.notificationService.emit('alerts.success', 'success')
                : null
            })
        }
      )
  }
}
